import { create } from "zustand";
import { apiClient } from "../lib/api-client";
import { wsClient } from "../lib/ws-client";
import type { User, LoginInput, RegisterInput, AuthResponse } from "../types/api";

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginInput) => Promise<void>;
  register: (data: RegisterInput, inviteToken?: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,

  login: async (credentials) => {
    const response = await apiClient.post<AuthResponse>("/auth/login", credentials);
    apiClient.setToken(response.token);
    wsClient.connect(response.token);
    set({ user: response.user, token: response.token, isAuthenticated: true });
  },

  register: async (data, inviteToken?) => {
    const endpoint = inviteToken ? `/auth/register/${inviteToken}` : "/auth/register";
    const response = await apiClient.post<AuthResponse>(endpoint, data);
    apiClient.setToken(response.token);
    wsClient.connect(response.token);
    set({ user: response.user, token: response.token, isAuthenticated: true });
  },

  logout: () => {
    apiClient.setToken(null);
    wsClient.disconnect();
    set({ user: null, token: null, isAuthenticated: false });
  },

  updateUser: (user) => set({ user }),
}));

async function initAuth() {
  const savedToken = apiClient.getToken();
  if (savedToken) {
    try {
      const response = await apiClient.get<{ user: User }>("/auth/me");
      wsClient.connect(savedToken);
      useAuthStore.setState({
        user: response.user,
        token: savedToken,
        isAuthenticated: true,
        isLoading: false,
      });
      return;
    } catch {
      apiClient.setToken(null);
    }
  }
  useAuthStore.setState({ isLoading: false });
}

initAuth();
